import { forwardRef, useState } from "react";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";

/**
 * @typedef {{
 *   children?: React.ReactNode,
 *   mode: "default" | "ghost"
 *   leftActiveText?: React.ReactNode,
 *   rightActiveText?: React.ReactNode,
 *   short?: boolean
 *   containerClassName?: string,
 *   leftActiveTextClassName?: string,
 *   rightActiveTextClassName?: string,
 *   error?: boolean
 * }} BaseInputProps
 * */

export const Input = forwardRef(
  /**
   * @param {BaseInputProps & React.InputHTMLAttributes<HTMLInputElement>} props
   * @return {import("react").ReactElement}
   */
  ({ className, leftActiveText, rightActiveText, onFocus, onBlur, mode, containerClassName, leftActiveTextClassName, rightActiveTextClassName, error, ...rest }, ref) => {
    const [focused, setFocused] = useState(false);

    const hasBottomText = !!leftActiveText || !!rightActiveText;

    return (
      <WithReset className={cn("relative", containerClassName)}>
        <WithReset
          as={"input"}
          ref={ref}
          className={cn(
            "w-full max-w-full rounded-[5px] font-inter text-app-grey-0 transition-[border,box-shadow] duration-100 ease-in-out bg-transparent leading-[1.4] px-2 py-2",
            "placeholder:text-app-grey-1",
            hasBottomText ? "mb-[1.2rem]" : "",
            error
              ? "border-[1.5px] border-app-danger-color focus:border-app-danger-color hover:border-app-danger-color"
              : mode === "ghost"
              ? "border border-transparent hover:border-app-grey-3 hover:shadow-tiny focus:border-app-primary-cta-high-contrast"
              : "border border-app-grey-3 focus:border-app-primary-cta-high-contrast focus:shadow-tiny",
            className
          )}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          {...rest}
        />

        {hasBottomText && (
          <div
            className={cn(
              "flex absolute left-0 right-0 bottom-0 px-[0.6rem] pb-0 font-medium text-app-grey-1 transition-[opacity,transform] duration-100",
              focused ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none translate-y-1",
              leftActiveText && !rightActiveText && "justify-start",
              rightActiveText && !leftActiveText && "justify-end",
              leftActiveText && rightActiveText && "justify-between"
            )}
          >
            {leftActiveText && (
              <span
                aria-readonly="true"
                contentEditable="false"
                className={cn("text-xs", leftActiveTextClassName)}
              >
                {leftActiveText}
              </span>
            )}

            {rightActiveText && (
              <span
                aria-readonly="true"
                contentEditable="false"
                className={cn("text-xs", rightActiveTextClassName)}
              >
                {rightActiveText}
              </span>
            )}
          </div>
        )}
      </WithReset>
    );
  }
);
